<template>
  <!-- Uma linha só: as abas e o botão principal da aba aberta. Já houve um grupo de ações a mais, que
       no telefone descia para uma segunda linha — só a aba Coleções o usava, para os controles do
       diagrama, e eles foram para dentro da caixa do desenho (escolha do usuário). -->
  <!-- Com uma aba só, sem a fileira: não há entre o que trocar (a ficha com uma seção só) -->
  <ontos-section :divider="false" :header="tabs.length > 1">
    <template #title>
      <!-- `overflow`: as que não couberem vão para o "Mais ▾" -->
      <ontos-view-tabs :views="tabs" :model-value="current" overflow @update:model-value="pick($event)"/>
    </template>
    <!-- O botão principal de cada aba, em azul e à direita: solto, com rótulo, como o "+ Criar X" da
         faixa do caminho (pedido do usuário). Abaixo do md, onde ele vira só ícone, com o contorno do
         grupo — o mesmo do lápis dos blocos logo embaixo (pedido do usuário): um "+" sem contorno
         ficava solto no canto. -->
    <!-- Depois dele, os controles da VISÃO da aba aberta (`tools`) — o mostrar encerrados e a
         Lista/Kanban de um relacionamento na ficha: na linha das abas, e não numa linha só deles
         embaixo, que gastava altura (pedido do usuário). O botão principal primeiro, e a troca de
         visão por último, na ponta direita (pedido do usuário). Fora do grupo do botão: não são
         ações. -->
    <template v-if="hasControls || hasTools" #controls>
      <template v-if="hasControls">
        <ontos-action-group v-if="$vuetify.display.smAndDown && !plainControls">
          <slot name="controls" :tab="current"/>
        </ontos-action-group>
        <slot v-else name="controls" :tab="current"/>
      </template>
      <span v-if="hasTools" class="tabbed-section-tools">
        <slot name="tools" :tab="current"/>
      </span>
    </template>
    <slot :tab="current"/>
  </ontos-section>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import Section from '@/components/shared/Section.vue'
import ActionGroup from '@/components/shared/ActionGroup.vue'
import ViewTabs, {ViewTab} from '@/components/shared/ViewTabs.vue'
import {tabQuery} from '@/utils/breadcrumb'
import {Comment, Fragment, VNode} from 'vue'

/** O nó desenha algo? O `v-if` falso vira comentário, e o `v-for` vazio, um fragmento sem filhos. */
function rendered(node: VNode): boolean {
  if (node.type === Comment) return false
  if (node.type === Fragment) return Array.isArray(node.children) && (node.children as VNode[]).some(rendered)
  return true
}

/**
 * Uma tela de partes em abas, no arranjo que o design do ambiente estreou e o da coleção repete: as
 * abas no lugar do título de uma seção só, o grupo de ações e o botão principal da aba aberta à
 * direita, e o conteúdo da aba embaixo. A aba aberta chega a cada slot pelo escopo (`tab`).
 *
 * Um componente porque são três telas — os dois designs e a ficha do item —, e todas têm de concordar em tudo — onde ficam os botões, o
 * que acontece no telefone, como a aba vai para a URL. Escrito em cada uma, divergiria no primeiro
 * ajuste.
 *
 * **A aba aberta é da URL quando a tela é a rota** (`routed`): o F5 e o voltar do navegador a mantêm,
 * e quem sai de uma parte volta a ela. Na janela, não: a URL ali é a da tela de trás, e a aba fica
 * guardada aqui. Trocar de aba é `replace`: o voltar não passa por cada uma.
 */
@Component({
  name: 'ontos-tabbed-section',
  components: {'ontos-section': Section, 'ontos-action-group': ActionGroup, 'ontos-view-tabs': ViewTabs}
})
class TabbedSection extends Vue {
  @Prop({type: Array as () => ViewTab[], required: true}) readonly tabs!: ViewTab[]
  /** A aba com que a tela abre, sem pedido na URL. */
  @Prop({type: String, required: true}) readonly opens!: string
  @Prop({type: Boolean, default: false}) readonly routed!: boolean
  /**
   * O botão principal solto também abaixo do md, sem o contorno do grupo. O contorno é dos designs,
   * onde o "+" ficava solto ao lado dos lápis com contorno dos blocos; na ficha, ao lado das abas, o
   * "Adicionar" segue o padrão dos outros "+", sem contorno (pedido do usuário).
   */
  @Prop({type: Boolean, default: false}) readonly plainControls!: boolean

  /** A da janela, que não tem URL. */
  chosen = ''

  get current(): string {
    const asked = this.routed ? this.$route.query.tab as string : this.chosen
    return this.tabs.some(tab => tab.value === asked) ? asked : this.opens
  }

  /**
   * A aba aberta tem botão principal? Só aí o lugar dele existe: no telefone o botão vai num grupo
   * com contorno, e uma aba sem botão — a dos campos, na ficha — ficava com o contorno vazio.
   */
  get hasControls(): boolean {
    return this.renders('controls')
  }

  /** A aba aberta tem controles da visão? Sem eles, nem o lugar deles. */
  get hasTools(): boolean {
    return this.renders('tools')
  }

  /** O slot desenha algo para a aba aberta? */
  private renders(slot: 'controls' | 'tools'): boolean {
    const render = this.$slots[slot]
    return !!render && render({tab: this.current}).some(rendered)
  }

  pick(tab: string) {
    if (this.routed) this.$router.replace({query: tabQuery(tab, this.opens)}).catch(() => undefined)
    else this.chosen = tab
  }
}

export default toNative(TabbedSection)
</script>

<style scoped>
/* Os controles da visão lado a lado, com o vão dos botões do cabeçalho */
.tabbed-section-tools {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
